import { Box, Flex, Spinner, Text } from '@chakra-ui/react';
import { useContext, useMemo, useState } from 'react';
import SessionContext from '../contexts/SessionContext';
import { useInternalOrganizations, type InternalOrganization } from '../hooks/useInternalOrganizations';
import { useSwitchOrganization } from '../hooks/useSwitchOrganization';
import { Button, CheckboxMenuList, CheckboxMenuSearch, Icon, MenuRow, MenuSectionLabel } from '../ui';

interface OrganizationSwitcherPanelProps {
    /** Inset panels (account menu) get a Back row; omit when the panel stands alone. */
    onBack?: () => void;
    /** Called just before the switch request, e.g. to park state that must survive a remount. */
    onSwitchStart?: (organization: InternalOrganization) => void;
    /** Called after the switch lands, or when the current org is picked again. */
    onSwitched?: (organization: InternalOrganization) => void;
    onSwitchError?: () => void;
    autoFocus?: boolean;
    listMaxH?: string;
}

/**
 * Searchable list of every customer org, for Yaxle employee accounts. Picking one
 * switches the whole account into that org. Shared by the account menu and platform search.
 */
const OrganizationSwitcherPanel = ({
    onBack,
    onSwitchStart,
    onSwitched,
    onSwitchError,
    autoFocus = true,
    listMaxH,
}: OrganizationSwitcherPanelProps) => {
    const sessionContext = useContext(SessionContext);
    const currentOrganizationId = sessionContext?.isAuthenticated ? sessionContext.organizationId : null;
    const [search, setSearch] = useState('');
    const { data, isLoading, isError } = useInternalOrganizations();
    const switchOrganization = useSwitchOrganization();

    const organizations = useMemo(() => {
        const all = data?.organizations ?? [];
        const needle = search.trim().toLowerCase();
        return needle ? all.filter((org) => org.name.toLowerCase().includes(needle)) : all;
    }, [data, search]);

    const handleSelect = (organization: InternalOrganization) => {
        if (organization.id === currentOrganizationId) {
            onSwitched?.(organization);
            return;
        }
        onSwitchStart?.(organization);
        switchOrganization.mutate({ organizationId: organization.id }, {
            onSuccess: () => onSwitched?.(organization),
            onError: () => onSwitchError?.(),
        });
    };

    return (
        <Box>
            {onBack ? (
                <Box p="6px" borderBottom="1px solid" borderColor="border.subtle">
                    <Button
                        variant="ghostGray"
                        size="sm"
                        leftIcon={<Icon name="chevronLeft" size="sm" />}
                        onClick={onBack}
                    >
                        Back
                    </Button>
                </Box>
            ) : null}
            <CheckboxMenuSearch
                value={search}
                onChange={setSearch}
                placeholder="Search organizations"
                autoFocus={autoFocus}
            />
            {isLoading ? (
                <Flex align="center" justify="center" py={6}>
                    <Spinner size="sm" color="fg.muted" />
                </Flex>
            ) : isError ? (
                <Text textStyle="body" color="fg.muted" px="14px" py={4}>
                    Couldn't load organizations.
                </Text>
            ) : (
                <>
                    <MenuSectionLabel>Organizations</MenuSectionLabel>
                    <CheckboxMenuList maxH={listMaxH}>
                        {organizations.length === 0 ? (
                            <Text textStyle="body" color="fg.muted" px="10px" py={2}>
                                No organizations match "{search.trim()}".
                            </Text>
                        ) : (
                            organizations.map((organization) => (
                                <MenuRow
                                    key={organization.id}
                                    kind="single"
                                    isSelected={organization.id === currentOrganizationId}
                                    isDisabled={switchOrganization.isPending}
                                    description={
                                        organization.organizationMode === 'invoicing'
                                            ? 'Invoicing'
                                            : organization.organizationMode === 'inventory'
                                              ? 'Inventory'
                                              : undefined
                                    }
                                    onClick={() => handleSelect(organization)}
                                >
                                    {organization.name}
                                </MenuRow>
                            ))
                        )}
                    </CheckboxMenuList>
                </>
            )}
        </Box>
    );
};

export default OrganizationSwitcherPanel;
